import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { LiveTvProviderSummary } from '../../api/types';
import type { ChannelFilter, EncryptionFilter, KindFilter } from './lineup';
import { useProviderName } from './lineup';

interface ChannelFilterBarProps {
  filter: ChannelFilter;
  onChange: (filter: ChannelFilter) => void;
  providers: LiveTvProviderSummary[] | undefined;
  /// Further controls that belong with the filters on this screen.
  children?: ReactNode;
}

/// Search, provider, TV or radio, free or scrambled. Wraps onto several lines on a phone rather
/// than scrolling sideways, and every control is a full-size touch target.
export default function ChannelFilterBar({ filter, onChange, providers, children }: ChannelFilterBarProps) {
  const { t } = useTranslation();
  const providerName = useProviderName(providers);

  return (
    <Stack direction="row" useFlexGap spacing={1} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
      <TextField
        size="small"
        value={filter.search}
        onChange={(event) => onChange({ ...filter, search: event.target.value })}
        placeholder={t('livetvChannels.filter.search')}
        slotProps={{
          htmlInput: { 'aria-label': t('livetvChannels.filter.search') },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
        sx={{ flex: '1 1 200px', minWidth: 0 }}
      />

      <TextField
        select
        size="small"
        label={t('livetvChannels.filter.provider')}
        value={filter.providerId}
        onChange={(event) => onChange({ ...filter, providerId: event.target.value })}
        sx={{ flex: '1 1 150px', minWidth: 0 }}
      >
        <MenuItem value="all">{t('livetvChannels.filter.allProviders')}</MenuItem>
        {(providers ?? []).map((provider) => (
          <MenuItem key={provider.providerId} value={provider.providerId}>
            {providerName(provider.providerId, provider.displayNameKey)}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        size="small"
        label={t('livetvChannels.filter.kind')}
        value={filter.kind}
        onChange={(event) => onChange({ ...filter, kind: event.target.value as KindFilter })}
        sx={{ flex: '1 1 120px', minWidth: 0 }}
      >
        <MenuItem value="all">{t('livetvChannels.filter.allKinds')}</MenuItem>
        <MenuItem value="Television">{t('livetvChannels.kind.Television')}</MenuItem>
        <MenuItem value="Radio">{t('livetvChannels.kind.Radio')}</MenuItem>
      </TextField>

      <TextField
        select
        size="small"
        label={t('livetvChannels.filter.encryption')}
        value={filter.encryption}
        onChange={(event) => onChange({ ...filter, encryption: event.target.value as EncryptionFilter })}
        sx={{ flex: '1 1 140px', minWidth: 0 }}
      >
        <MenuItem value="all">{t('livetvChannels.filter.allEncryption')}</MenuItem>
        <MenuItem value="free">{t('livetvChannels.filter.free')}</MenuItem>
        <MenuItem value="encrypted">{t('livetvChannels.filter.encrypted')}</MenuItem>
      </TextField>

      {children}
    </Stack>
  );
}
